import CartItem from "../models/CartItem";
import Coupon from "../models/Coupon";
import { getProductBySku, getVariantBySku } from "./entityUtils";
import { isBooleanTrue, isListNotEmpty, isNumberGreaterThanZero, isObjectNotEmpty, isStringNotEmpty } from "./objectUtils";
import { isSinglePaymentPlanOnly } from "./couponEligibilityUtils";

export const getCartItemDiscount = (coupon, product, products, variants, startingLevel, taxRate = 0, quantity = 1, bundleCount = 1) => {
    if (isObjectNotEmpty(product) && isListNotEmpty(variants) && isObjectNotEmpty(coupon) && ['CART', 'PRODUCT', 'MIXED'].includes(coupon?.discountTarget)) {
        let variant = variants.find(i => i.productId === product?.id && isBooleanTrue(i.enabled) && i.titrationLevel === startingLevel);
        let cartItem = new CartItem({
            id: product?.id,
            sku: product?.sku,
            variantSku: variant?.sku,
            variantId: variant?.id,
            quantity: quantity,
            bundleCount: bundleCount
        });

        let cartItems = [cartItem];

        return CartItem.getCartItemDiscount(coupon, cartItems, cartItem, taxRate, products, variants).discountTotal;
    }

    return 0;
};

export const getCartItemRecurringDiscount = (coupon, product, products, variants, startingLevel, taxRate = 0, quantity = 1, bundleCount = 1) => {
    if (isObjectNotEmpty(product) && isListNotEmpty(variants) && isObjectNotEmpty(coupon) && ['RECURRING', 'MIXED_RECURRING', 'BUNDLE'].includes(coupon?.discountTarget)) {
        let variant = variants.find(i => i.productId === product?.id && isBooleanTrue(i.enabled) && i.titrationLevel === startingLevel);
        let cartItem = new CartItem({
            id: product?.id,
            sku: product?.sku,
            variantSku: variant?.sku,
            variantId: variant?.id,
            quantity: quantity,
            bundleCount: bundleCount
        });

        let cartItems = [cartItem];

        return CartItem.getCartItemDiscount(coupon, cartItems, cartItem, taxRate, products, variants).discountTotal;
    }

    return 0;
};

export const getCartItemDiscountWithVariant = (coupon, productSku, variantSku, products, variants, taxRate = 0, quantity = 1, bundleCount = 1) => {
    if (isObjectNotEmpty(coupon) && ['CART', 'PRODUCT', 'MIXED'].includes(coupon?.discountTarget)) {
        let product = getProductBySku(productSku, products);
        let variant = getVariantBySku(variantSku, variants?.filter(i => isBooleanTrue(i.enabled)));
        let cartItem = new CartItem({
            id: product?.id,
            sku: product?.sku,
            variantSku: variant?.sku,
            variantId: variant?.id,
            quantity: quantity,
            bundleCount: bundleCount
        });

        let cartItems = [cartItem];

        return CartItem.getCartItemDiscount(coupon, cartItems, cartItem, taxRate, products, variants).discountTotal;
    }

    return 0;
};

export const getCartItemDiscountWithProductAndVariant = (coupon, product, variantSku, products, variants, taxRate = 0, quantity = 1, bundleCount = 1) => {
    if (isObjectNotEmpty(product) && isObjectNotEmpty(coupon) && ['CART', 'PRODUCT', 'MIXED'].includes(coupon?.discountTarget)) {
        let variant = getVariantBySku(variantSku, variants?.filter(i => isBooleanTrue(i.enabled)));
        let cartItem = new CartItem({
            id: product?.id,
            sku: product?.sku,
            variantSku: variant?.sku,
            variantId: variant?.id,
            quantity: quantity,
            bundleCount: bundleCount
        });

        let cartItems = [cartItem];

        return CartItem.getCartItemDiscount(coupon, cartItems, cartItem, taxRate, products, variants).discountTotal;
    }

    return 0;
};

export const getCartItemRecurringDiscountWithProductAndVariant = (coupon, product, variantSku, products, variants, taxRate = 0, quantity = 1, bundleCount = 1) => {
    if (isObjectNotEmpty(product) && isListNotEmpty(variants) && isObjectNotEmpty(coupon) && ['RECURRING', 'MIXED_RECURRING', 'BUNDLE'].includes(coupon?.discountTarget)) {
        let variant = getVariantBySku(variantSku, variants?.filter(i => isBooleanTrue(i.enabled)));
        let cartItem = new CartItem({
            id: product?.id,
            sku: product?.sku,
            variantSku: variant?.sku,
            variantId: variant?.id,
            quantity: quantity,
            bundleCount: bundleCount
        });

        let cartItems = [cartItem];

        return CartItem.getCartItemDiscount(coupon, cartItems, cartItem, taxRate, products, variants).discountTotal;
    }

    return 0;
};

export const getCouponCodeDiscount = async (couponCode, product, products, variants, startingLevel, taxRate = 0, quantity = 1, bundleCount = 1) => {
    if (isObjectNotEmpty(product) && isListNotEmpty(variants) && isStringNotEmpty(couponCode)) {
        const coupon = await Coupon.getByCode(couponCode);
        if (isObjectNotEmpty(coupon) && ['CART', 'PRODUCT', 'MIXED'].includes(coupon?.discountTarget)) {
            let variant = variants.find(i => i.productId === product?.id && isBooleanTrue(i.enabled) && i.titrationLevel === startingLevel) ?? null;
            let cartItem = new CartItem({
                id: product?.id,
                sku: product?.sku,
                variantSku: variant?.sku,
                variantId: variant?.id ?? null,
                quantity: quantity,
                bundleCount: bundleCount
            });

            let cartItems = [cartItem];

            return CartItem.getCartItemDiscount(coupon, cartItems, cartItem, taxRate, products, variants).discountTotal;
        }
    }

    return 0;
};

// a stored bundle count outlives the product it was chosen from: a bundle discount can be withdrawn while
// a cart sits abandoned. two things go wrong on resume without this. `Number(undefined)` is NaN and `??`
// does not catch NaN, so `Number(x) ?? 1` yields NaN and every price derived from it follows. And a count
// the product no longer offers renders no matching radio, leaving a checkout priced for a bundle the
// customer cannot see selected. month-to-month is always offered, so 1 is the safe floor
export const toSelectableBundleCount = (bundleCount, product) => {
    const count = Number(bundleCount);
    if (isNumberGreaterThanZero(count) === false) {
        return 1;
    }

    const offeredCounts = isListNotEmpty(product?.bundleDiscounts)
        ? product.bundleDiscounts.map(i => Number(i?.bundlePaymentCount))
        : [];

    return (count === 1 || offeredCounts.includes(count)) ? count : 1;
};

export const toMaxBundleCount = (product, profile) => {
    if (isObjectNotEmpty(profile) && isBooleanTrue(profile?.isFriendsAndFamily)) {
        return 1;
    }

    const offeredCounts = isListNotEmpty(product?.bundleDiscounts)
        ? product?.bundleDiscounts.map(i => Number(i?.bundlePaymentCount))
        : [];

    return offeredCounts.length > 0 ? Math.max(...offeredCounts) : 1;
};

// what the plan step calls a product's single-payment plan - a 90-day product reads 90-Day on every plan
export const getSinglePaymentPlanLabel = (product) => {
    return (product?.sku?.includes('-90') || product?.sku === 'METFORMIN') ? '90-Day' : 'Monthly';
};

// whether the attached coupon is what holds the plan list to the single-payment plan - a product with a longer plan,
// under a recurring coupon that cannot discount one - so the plan step can say why the longer plans are gone
export const isCouponHoldingSinglePaymentPlan = (product, profile, coupon) => {
    return toMaxBundleCount(product, profile) > 1 && isSinglePaymentPlanOnly({ coupon });
};